/**
 * Read an image from the system clipboard and materialize it as a local
 * file object suitable for the existing upload / attach pipeline.
 *
 * Web paste is handled separately via the browser ClipboardEvent in
 * ChatInputTextarea. This helper is for native (iOS/Android) where RN
 * TextInput does not surface image paste events.
 */

import { Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import * as FileSystem from 'expo-file-system';

/**
 * expo-clipboard already prepends `data:image/png;base64,` (or jpeg).
 * Some iOS versions also insert charset / extra params before `base64,`.
 * If we fail to strip that header, writeAsStringAsync stores garbage and
 * the composer can show a local preview while the upload never finishes.
 */
export function parseClipboardImageData(data) {
  if (typeof data !== 'string' || !data) return null;

  const comma = data.indexOf(',');
  const hasDataUri = data.startsWith('data:') && comma !== -1;
  const header = hasDataUri ? data.slice(0, comma) : '';
  const base64 = (hasDataUri ? data.slice(comma + 1) : data).replace(/\s/g, '');
  if (!base64) return null;

  const formatMatch = header.match(/image\/(png|jpeg|jpg)/i);
  const rawFormat = (formatMatch?.[1] || 'png').toLowerCase();
  const format = rawFormat === 'jpg' ? 'jpeg' : rawFormat === 'jpeg' ? 'jpeg' : 'png';
  return { base64, format };
}

/**
 * @returns {Promise<boolean>}
 */
export async function clipboardHasImage() {
  if (Platform.OS === 'web' || Platform.OS === 'macos') return false;
  if (typeof Clipboard.hasImageAsync !== 'function') return false;
  try {
    return await Clipboard.hasImageAsync();
  } catch {
    return false;
  }
}

/**
 * Read clipboard image and write it to the cache directory.
 * Shape matches ImagePicker / DocumentPicker assets so the library
 * upload path can attach it without a special case.
 * @returns {Promise<{ uri: string, name: string, type: string, mimeType: string, size: number } | null>}
 */
export async function getClipboardImageAsFile() {
  if (Platform.OS === 'web' || Platform.OS === 'macos') return null;
  if (typeof Clipboard.getImageAsync !== 'function') return null;

  let image;
  try {
    image = await Clipboard.getImageAsync({ format: 'png' });
  } catch (error) {
    console.error('[clipboardImage] getImageAsync failed:', error);
    return null;
  }

  const parsed = parseClipboardImageData(image?.data);
  if (!parsed) return null;

  const ext = parsed.format === 'jpeg' ? 'jpg' : 'png';
  const mimeType = parsed.format === 'jpeg' ? 'image/jpeg' : 'image/png';
  const name = `clipboard_${Date.now()}.${ext}`;
  const cacheDir = FileSystem.cacheDirectory || FileSystem.documentDirectory;
  if (!cacheDir) return null;

  const uri = `${cacheDir}${name}`;
  const encoding = FileSystem.EncodingType?.Base64 || 'base64';
  try {
    await FileSystem.writeAsStringAsync(uri, parsed.base64, { encoding });
  } catch (error) {
    console.error('[clipboardImage] Failed to write cache file:', error);
    return null;
  }

  let size = Math.floor((parsed.base64.length * 3) / 4);
  try {
    const info = await FileSystem.getInfoAsync(uri);
    if (!info?.exists) {
      console.error('[clipboardImage] Cache file missing after write:', uri);
      return null;
    }
    if (typeof info.size === 'number') {
      size = info.size;
    }
  } catch {
    // Keep base64 estimate
  }

  // Android OkHttp / RN FormData often cannot read app-private file://
  // cache paths. ImagePicker works because it returns content://. Convert
  // so the same upload path as the photo library can actually finish.
  let uploadUri = uri;
  if (Platform.OS === 'android' && typeof FileSystem.getContentUriAsync === 'function') {
    try {
      uploadUri = await FileSystem.getContentUriAsync(uri);
    } catch (error) {
      console.warn('[clipboardImage] getContentUriAsync failed; using file URI:', error?.message || error);
    }
  }

  return {
    uri: uploadUri,
    name,
    type: mimeType,
    mimeType,
    size,
  };
}
